import Button from '@/components/Buttons/Button';
import theme from '@/theme';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { TextField } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StarRating } from './StarRating';

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const fadeUp = keyframes`
    from {
        transform: translateY(25%);
    }
    to {
        transform: translateY(0);
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
`;

const FeedbackContainer = styled.div<{ isVisible: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: radial-gradient(circle, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.8) 100%);
    animation: ${props => (props.isVisible ? fadeIn : fadeOut)} 0.5s forwards;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FeedbackTitle = styled.div`
    font-size: 1.6rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.white};
    animation: ${fadeIn} 0.5s;
`;

const SuccessTitle = styled(FeedbackTitle)`
    animation:
        ${fadeUp} 0.5s,
        ${fadeIn} 0.5s;
`;

const FeedbackDescription = styled.div`
    font-size: 1.8rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.white};
    white-space: pre-line;
    text-align: center;
`;

const FeedbackHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
`;

const StyledTextField = styled(TextField)`
    border-radius: 8px;
    background-color: rgba(9, 6, 4, 0.8);
    color: ${theme.colors.white};

    & .MuiOutlinedInput-root {
        & fieldset {
            border-color: ${theme.colors.black[800]} !important;
        }
    }

    textarea {
        min-height: 66px;
        width: 250px;
        font-size: 1.25rem;
        caret-color: ${theme.colors.primary[900]};
        color: ${theme.colors.white};
        line-height: 1.8rem;
        ::placeholder {
            color: ${theme.colors.white};
        }
    }
`;

const FeedbackForm = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
`;

const FeedbackResult = styled.div<{ isVisible: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    transition:
        opacity 0.5s,
        height 0.5s;
    pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
    height: ${({ isVisible }) => (isVisible ? 'auto' : 0)};
`;

const FeedbackContent = styled.div`
    display: flex;
    padding: 12px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
`;

const SubmitButton = styled(Button)`
    margin-left: auto;
`;

type ReviewType = 'negative' | 'neutral' | 'positive';

interface VideoFeedbackProps {
    placeholder?: Record<ReviewType, string>;
    description?: Record<ReviewType, string>;
    onSubmit: (props: { starRating: number; feedback?: string }) => void;
    onStarRatingChange: (rating: number) => void;
    onClose: () => void;
}

const getReviewType = (starRating?: number): ReviewType | undefined => {
    if (!starRating) return undefined;
    if (starRating < 3) return 'negative';
    if (starRating < 5) return 'neutral';
    return 'positive';
};

/** Used to prevent the feedback from closing when clicking on the content */
const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
};

export const VideoFeedback = ({
    /** Used to customize difference messages between video and avatar feedbacks */
    description = {
        negative: 'Sorry to hear that, what can be improved?',
        neutral: 'Thank you for your feedback.\nCould you let us know what we can improve?',
        positive: "We're glad you liked it, what can we improve?",
    },
    placeholder = {
        negative: 'Which part of the video can be improved?',
        neutral: 'Which part of the video can be improved?',
        positive: 'Which part of the video stood out?',
    },
    onSubmit,
    onStarRatingChange,
    onClose,
}: VideoFeedbackProps) => {
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [starRating, setStarRating] = useState<number | undefined>(undefined);
    const [feedback, setFeedback] = useState<string | undefined>(undefined);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (!isClosing) return;

        const timeout = setTimeout(() => {
            onClose();
        }, 500);

        return () => clearTimeout(timeout);
    }, [isClosing, onClose]);

    const handleClose = useCallback(() => {
        setIsClosing(true);
    }, []);

    const handleSetStarRating = useCallback(
        (rating: number) => {
            setStarRating(rating);
            onStarRatingChange?.(rating);
        },
        [onStarRatingChange]
    );

    const reviewType = useMemo(() => getReviewType(starRating), [starRating]);

    const handleSubmit = useCallback(() => {
        if (!starRating) return;
        setShowSuccessMessage(true);
        onSubmit({ starRating, feedback });
    }, [starRating, feedback, onSubmit]);

    const handleOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFeedback(e.target.value);
    }, []);

    if (showSuccessMessage) {
        return (
            <FeedbackContainer isVisible={!isClosing} onClick={handleClose}>
                <FeedbackContent onClick={stopPropagation}>
                    <SuccessTitle>Thank you!</SuccessTitle>
                </FeedbackContent>
            </FeedbackContainer>
        );
    }

    return (
        <FeedbackContainer isVisible={!isClosing} onClick={handleClose}>
            <FeedbackContent onClick={stopPropagation}>
                <FeedbackHeader>
                    <FeedbackTitle>How would you rate this Avatar?</FeedbackTitle>
                    <StarRating starRating={starRating} setStarRating={handleSetStarRating} numberOfStars={5} />
                </FeedbackHeader>
                <FeedbackResult isVisible={!!reviewType}>
                    <FeedbackDescription>{description[reviewType ?? 'neutral']}</FeedbackDescription>
                    <FeedbackForm>
                        <StyledTextField
                            autoFocus
                            multiline
                            size="small"
                            value={feedback}
                            onChange={handleOnChange}
                            placeholder={placeholder[reviewType ?? 'neutral']}
                        />
                        <SubmitButton color="secondary" onClick={handleSubmit}>
                            Submit
                        </SubmitButton>
                    </FeedbackForm>
                </FeedbackResult>
            </FeedbackContent>
        </FeedbackContainer>
    );
};
